/* ============================================================
   ui.jsx — shared presentational components.
   ============================================================ */

import { useEffect, useState } from 'react';
import { Icon } from './Icon.jsx';

export const LOGO = '/icons/sif-logo.png';

/* ---- club logo lockup ---- */
const BRAND_KINDS = {
  kiosk:      { sub: 'KIOSK', accent: null, tracking: '2.5px' },
  admin:      { sub: 'ADMIN', accent: 'var(--orange)', tracking: '2.5px' },
};

export function Brand({ kind = 'kiosk', size = 46, light = false }) {
  const { sub, accent, tracking } = BRAND_KINDS[kind] || BRAND_KINDS.kiosk;
  return (
    <div className="brand-lockup">
      <img className="brand-logo" src={LOGO} style={{ width: size, height: size }} alt="SIF" />
      <div style={{ lineHeight: 1.05, minWidth: 0 }}>
        <div className="brand-name" style={{ fontSize: size * 0.5, color: light ? '#fff' : 'var(--green-deep)' }}>SIF</div>
        <div style={{
          fontFamily: 'var(--display)', fontWeight: 700, fontSize: size * 0.3, letterSpacing: tracking,
          whiteSpace: 'nowrap',
          color: accent || (light ? 'rgba(255,255,255,.8)' : 'var(--green)'),
        }}>{sub}</div>
      </div>
    </div>
  );
}

/* ---- modal shell ---- */
/* closeOnBackdrop={false}: formulär där ett felklick bredvid inte får kasta
   det man fyllt i — då stänger bara krysset och Avbryt. */
export function Modal({ title, onClose, children, footer, wide, closeOnBackdrop = true }) {
  useEffect(() => {
    const h = e => { if (e.key === 'Escape') onClose && onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  return (
    <div className="overlay" onMouseDown={e => { if (closeOnBackdrop && e.target === e.currentTarget) onClose && onClose(); }}>
      <div className="modal" style={wide ? { width: 'min(760px,100%)' } : {}}>
        <div className="modal-head">
          <div style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 21, flex: 1, minWidth: 0 }}>{title}</div>
          <button className="btn btn-icon btn-ghost" onClick={onClose}><Icon name="x" size={20} /></button>
        </div>
        <div className="scroll modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>
  );
}

/* ---- qty stepper ---- */
export function Stepper({ value, onChange, min = 0, step = 1, small }) {
  const btn = small ? 34 : 42;
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', background: '#fff', border: '1.5px solid var(--line)', borderRadius: small ? 10 : 12, overflow: 'hidden' }}>
      <button type="button" className="center" style={{ width: btn, height: btn, border: 'none', background: 'transparent', color: 'var(--green-deep)' }}
        onClick={() => onChange(Math.max(min, value - step))}><Icon name="minus" size={small ? 16 : 18} /></button>
      <div style={{ minWidth: small ? 28 : 38, textAlign: 'center', fontWeight: 800, fontSize: small ? 15 : 17 }}>{value}</div>
      <button type="button" className="center" style={{ width: btn, height: btn, border: 'none', background: 'transparent', color: 'var(--green-deep)' }}
        onClick={() => onChange(value + step)}><Icon name="plus" size={small ? 16 : 18} /></button>
    </div>
  );
}

/* ---- "gratis"-tagg på ett köp från Domare & ledare ----
   Visas i både adminloggen och kioskens Sålt idag, så den bor här. */
const FREE_TAGS = {
  domare: { emoji: '🧑‍⚖️', label: 'Domare', bg: '#e3ecf4', fg: '#2c5a7a' },
  ledare: { emoji: '☕', label: 'Ledare', bg: '#efe6d8', fg: '#7a5a2c' },
};

export function PaymentTag({ payment }) {
  const t = FREE_TAGS[payment];
  if (!t) return null;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: t.bg, color: t.fg, borderRadius: 8, padding: '4px 9px', fontSize: 13, fontWeight: 800 }}>
      {t.emoji} {t.label} · gratis
    </span>
  );
}

/* ---- raderna i ett köp som små piller ----
   Tillval som ingick (drickan i målet) står till 0 kr och märks "ingår". */
export function SaleItems({ items }) {
  return (items || []).map((it, j) => (
    <span key={j} style={{
      display: 'inline-flex', alignItems: 'center', gap: 5, borderRadius: 8, padding: '4px 9px', fontSize: 13, fontWeight: 700,
      background: it.included ? '#fbf6ec' : '#f4ecdc',
      border: it.included ? '1px dashed var(--line)' : '1px solid transparent',
      color: it.included ? 'var(--ink-soft)' : 'inherit',
    }}>
      <span>{it.emoji}</span>{it.qty > 1 ? `${it.qty}× ` : ''}{it.name}{it.included ? ' · ingår' : ''}
    </span>
  ));
}

/* ---- product thumb (emoji or coloured monogram) ---- */
export function Thumb({ p, size = 54, radius = 14 }) {
  const style = {
    width: size, height: size, borderRadius: radius, display: 'flex', alignItems: 'center',
    justifyContent: 'center', fontSize: size * 0.5, flexShrink: 0, background: '#fff',
  };
  if (p.emoji) return <div style={{ ...style, background: '#fbf6ec' }}>{p.emoji}</div>;
  return (
    <div style={{
      ...style, background: (p.color || '#3f8197') + '22', color: p.color || '#3f8197',
      fontWeight: 800, fontSize: size * 0.4, fontFamily: 'var(--display)',
    }}>{(p.name || '?').slice(0, 1)}</div>
  );
}

/* ---- bar chart (revenue series) ---- */
export function BarChart({ data, valueKey = 'rev', labelKey = 'week', height = 150, color = 'var(--green)', prefix = 'v.', onBarClick, selected }) {
  const max = Math.max(1, ...data.map(d => d[valueKey]));
  const sel = selected != null ? selected : data.length - 1;
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height, paddingTop: 8 }}>
      {data.map((d, i) => {
        const h = Math.max(3, (d[valueKey] / max) * (height - 34));
        const active = i === sel;
        return (
          <div key={i} onClick={onBarClick ? () => onBarClick(i) : undefined}
            style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, minWidth: 0, cursor: onBarClick ? 'pointer' : 'default' }}>
            <div style={{ fontSize: 10.5, fontWeight: 700, color: active ? 'var(--green-deep)' : 'var(--muted)', whiteSpace: 'nowrap' }}>
              {d[valueKey] > 0 ? (d[valueKey] >= 1000 ? Math.round(d[valueKey] / 1000) + 'k' : d[valueKey]) : ''}
            </div>
            <div style={{
              width: '100%', maxWidth: 34, height: h, borderRadius: '7px 7px 4px 4px',
              background: active ? color : '#cfe0d4', transition: 'height .3s, background .15s',
            }} />
            <div style={{ fontSize: 10.5, color: active ? 'var(--green-deep)' : 'var(--muted)', fontWeight: active ? 800 : 600, whiteSpace: 'nowrap' }}>
              {prefix}{d[labelKey]}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ---- stat / KPI tile ---- */
export function Stat({ label, value, sub, icon, accent = 'var(--green)', tone }) {
  return (
    <div className="card stat">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div className="center" style={{ width: 38, height: 38, borderRadius: 11, background: accent + '18', color: accent, flexShrink: 0 }}>
          <Icon name={icon} size={20} />
        </div>
        <div className="stat-label">{label}</div>
      </div>
      <div className="stat-value" style={{ color: tone || 'var(--ink)' }} title={String(value)}>{value}</div>
      {sub && <div className="stat-sub">{sub}</div>}
    </div>
  );
}

/* ---- emoji picker ----
   The full searchable catalogue, tagged with Swedish keywords. The quick
   sets below are what shows before the user starts searching. */
const EMOJI_CATALOG = [
  /* varm mat & grill */
  ['🌭', 'korv varmkorv hotdog grill bröd'], ['🍔', 'hamburgare burgare mål grill'], ['🍟', 'pommes frites strips'],
  ['🍕', 'pizza slice'], ['🥪', 'macka smörgås sandwich toast'], ['🌮', 'taco'], ['🌯', 'burrito wrap kebab'],
  ['🥙', 'pita kebab falafel'], ['🍗', 'kyckling klubba'], ['🍖', 'kött revben'], ['🥓', 'bacon'], ['🍳', 'ägg stekt frukost'],
  ['🥩', 'biff stek kött'], ['🍢', 'spett grillspett oden'], ['🍤', 'räka friterad tempura'], ['🥘', 'gryta paella panna'],
  ['🍲', 'soppa gryta'], ['🍛', 'curry ris gryta'], ['🍚', 'ris'], ['🍝', 'pasta spagetti'], ['🍜', 'nudlar ramen soppa'],
  ['🥟', 'dumpling pirog knyte'], ['🍥', 'fiskkaka'], ['🥫', 'konserv burk soppa'], ['🌽', 'majs majskolv'],
  /* bröd & bak */
  ['🥐', 'croissant giffel frukost bakverk'], ['🥨', 'kringla pretzel salt'], ['🥯', 'bagel fralla'],
  ['🍞', 'bröd limpa toast'], ['🥖', 'baguette bröd'], ['🫓', 'tunnbröd flatbread pita'], ['🥞', 'pannkakor plättar'],
  ['🧇', 'våffla våfflor'], ['🧈', 'smör'], ['🥚', 'ägg'], ['🧀', 'ost'],
  /* hembakat & sött */
  ['🍪', 'kaka kakor cookie hembakat fika'], ['🧁', 'muffins cupcake hembakat fika'], ['🍩', 'munk donut'],
  ['🍰', 'tårta kaka bit fika'], ['🎂', 'tårta födelsedag'], ['🥧', 'paj äppelpaj fika'], ['🍮', 'pudding flan'],
  ['🍯', 'honung'], ['🥮', 'kaka bakverk'], ['🍫', 'choklad kakao ruta'], ['🍬', 'godis karamell'],
  ['🍭', 'klubba slickepinne godis'], ['🍡', 'spett godis'], ['🍧', 'isglass strut shaved ice'],
  ['🍨', 'glass skål kula'], ['🍦', 'glass mjukglass strut'], ['🍿', 'popcorn'], ['🥠', 'kaka lyckokaka'],
  ['🧂', 'salt krydda'],
  /* frukt & grönt */
  ['🍎', 'äpple frukt'], ['🍏', 'äpple grönt frukt'], ['🍐', 'päron frukt'], ['🍊', 'apelsin clementin frukt'],
  ['🍋', 'citron frukt'], ['🍌', 'banan frukt'], ['🍉', 'vattenmelon frukt'], ['🍇', 'vindruvor druvor frukt'],
  ['🍓', 'jordgubbe jordgubbar bär'], ['🫐', 'blåbär bär'], ['🍈', 'melon frukt'], ['🍒', 'körsbär bär'],
  ['🍑', 'persika frukt'], ['🥭', 'mango frukt'], ['🍍', 'ananas frukt'], ['🥥', 'kokos kokosnöt'],
  ['🥝', 'kiwi frukt'], ['🍅', 'tomat'], ['🥑', 'avokado'], ['🥒', 'gurka'], ['🥕', 'morot'],
  ['🥬', 'sallad grönsak'], ['🥦', 'broccoli'], ['🧅', 'lök rostad'], ['🧄', 'vitlök'], ['🥔', 'potatis'],
  ['🍠', 'sötpotatis'], ['🌶️', 'chili stark'], ['🫑', 'paprika'], ['🥗', 'sallad skål'], ['🥜', 'jordnötter nötter'],
  ['🌰', 'kastanj nöt'], ['🍄', 'svamp champinjon'],
  /* dryck */
  ['☕', 'kaffe fika varm dryck'], ['🍵', 'te varm dryck'], ['🫖', 'tekanna te'], ['🥤', 'läsk dricka mugg sugrör cola'],
  ['🧃', 'juice festis dricka kartong'], ['🧋', 'bubbelte boba milkshake'], ['🥛', 'mjölk glas'],
  ['💧', 'vatten droppe'], ['🚰', 'vatten kran'], ['🫙', 'burk flaska'], ['🍼', 'nappflaska mjölk'],
  ['🧉', 'mate dryck'], ['🍶', 'flaska sake'], ['🍾', 'flaska champagne fest'], ['🍺', 'öl'], ['🍹', 'drink saft'],
  ['🍸', 'drink'], ['🧊', 'is isbit kall'], ['🥂', 'skål glas fest'], ['🍷', 'vin glas'],
  /* sport & förening */
  ['⚽', 'fotboll boll match'], ['🏒', 'hockey klubba'], ['🏀', 'basket boll'], ['🏐', 'volleyboll boll'],
  ['🎾', 'tennis boll'], ['⚾', 'baseboll boll'], ['🥏', 'frisbee disc'], ['🏓', 'pingis bordtennis'],
  ['🏸', 'badminton'], ['🥅', 'mål målbur'], ['🥇', 'guld medalj vinst'], ['🥈', 'silver medalj'],
  ['🥉', 'brons medalj'], ['🏆', 'pokal cup vinst'], ['🎽', 'tröja matchtröja väst'], ['👕', 'tröja tshirt kläder'],
  ['🧢', 'keps mössa'], ['🧣', 'halsduk supporter'], ['🧤', 'handskar vantar'], ['🎿', 'skidor vinter'],
  ['⛸️', 'skridskor is'], ['🛹', 'skateboard'], ['🚲', 'cykel'], ['🏃', 'löpning springa'], ['💪', 'styrka muskel'],
  ['🙌', 'heja jubel'], ['👏', 'applåd heja'], ['📣', 'megafon heja rop'], ['🎉', 'fest konfetti firande'],
  ['🎈', 'ballong fest'], ['🚩', 'flagga hörnflagga'], ['🏁', 'målflagg racing'], ['🇸🇪', 'sverige flagga'],
  /* kiosk & prylar */
  ['🏷️', 'prislapp etikett rea'], ['💰', 'pengar kassa säck'], ['💵', 'sedel pengar kontant'],
  ['🪙', 'mynt växel'], ['🧾', 'kvitto'], ['🛒', 'kundvagn köp'], ['🧺', 'korg'], ['🎫', 'biljett entré'],
  ['🎟️', 'biljett lott'], ['🎁', 'present paket lotteri'], ['🔑', 'nyckel lås'], ['🔒', 'lås låst'],
  ['📦', 'paket kartong låda'], ['🍽️', 'tallrik bestick mat'], ['🥄', 'sked'], ['🍴', 'bestick gaffel kniv'],
  ['🥢', 'ätpinnar'], ['🔥', 'eld grill varm'], ['♨️', 'ånga varm'], ['🧯', 'brandsläckare'],
  ['⏲️', 'timer klocka äggklocka'], ['🕐', 'klocka tid'], ['📋', 'lista urklipp schema'], ['✏️', 'penna skriva'],
  ['📌', 'nål anslag'], ['📍', 'plats nål'], ['📱', 'mobil telefon swish'], ['💳', 'kort betalkort betala'],
  ['🖨️', 'skrivare'], ['💡', 'lampa glödlampa idé'], ['🔋', 'batteri'], ['🔌', 'kontakt sladd el'],
  ['🔦', 'ficklampa'], ['📢', 'högtalare utrop'], ['🎵', 'musik not'], ['🎤', 'mikrofon'],
  /* städ & förbrukning */
  ['🧻', 'toapapper hushållspapper papper'], ['🧼', 'tvål'], ['🧴', 'flaska diskmedel handsprit lotion'],
  ['🧽', 'svamp disksvamp'], ['🧹', 'kvast sopa städ'], ['🪣', 'hink'], ['🗑️', 'soptunna skräp svinn'],
  ['🚿', 'dusch'], ['🪥', 'tandborste'], ['🪒', 'rakhyvel'], ['🩹', 'plåster första hjälpen'],
  ['💊', 'medicin tablett'], ['🩺', 'sjukvård'], ['🪛', 'skruvmejsel verktyg'], ['🔧', 'skiftnyckel verktyg'],
  ['🔨', 'hammare verktyg'], ['🪚', 'såg verktyg'], ['🪜', 'stege'], ['🪵', 'ved trä'], ['🖌️', 'pensel måla'],
  ['🎨', 'färg måla palett'], ['📏', 'linjal mått'], ['🚽', 'toalett wc'], ['🧷', 'säkerhetsnål'],
  ['🕯️', 'ljus stearinljus'], ['⛽', 'bensin bränsle'],
  /* väder & övrigt */
  ['☀️', 'sol sommar'], ['⛅', 'moln halvklart'], ['🌧️', 'regn'], ['❄️', 'snö vinter kall'],
  ['🌈', 'regnbåge'], ['⭐', 'stjärna favorit'], ['✨', 'glitter nytt'], ['❤️', 'hjärta kärlek'],
  ['💚', 'hjärta grönt klubben'], ['🧡', 'hjärta orange'], ['👍', 'tumme bra'], ['🤝', 'handslag samarbete'],
  ['🐶', 'hund'], ['🐱', 'katt'], ['🦆', 'anka fågel'], ['🐝', 'bi humla'], ['🌸', 'blomma körsbärsblom'],
  ['🌻', 'solros blomma'], ['🍀', 'klöver tur lycka'], ['🌲', 'gran träd skog'], ['🎄', 'julgran jul'],
  ['🎃', 'pumpa halloween'], ['🐣', 'kyckling påsk'], ['🥳', 'fest partyhatt'], ['😀', 'glad leende smiley'],
  ['😋', 'mums gott smiley'], ['🤩', 'stjärnögon wow'],
];

const EMOJI_SET = ['🌭', '🍔', '🍟', '🧀', '🥪', '🍕', '🥨', '🥐', '🧇', '🥞', '🍪', '🧁', '🍩', '🍰', '🥧', '☕', '🍵', '🥤', '🧃', '💧', '🧊', '🍺', '🍬', '🍫', '🍭', '🍿', '🥜', '🍌', '🍎', '🍇', '🍦', '🌮', '🥗', '🍝', '🍜', '🥟', '🏷️', '🍽️'];

export function EmojiPick({ value, onChange, options = EMOJI_SET }) {
  const [q, setQ] = useState('');
  const query = q.trim().toLowerCase();

  /* Utan sökning visas snabbvalen; en sökning letar i hela katalogen. */
  const shown = query
    ? EMOJI_CATALOG.filter(([, kw]) => kw.includes(query)).map(([e]) => e)
    : options;

  return (
    <div>
      <input className="input" value={q} onChange={e => setQ(e.target.value)}
        placeholder="🔍 Sök symbol… t.ex. glass, kaffe, boll" style={{ padding: '10px 13px', fontSize: 14.5, marginBottom: 9 }} />
      <div className="emoji-grid scroll" style={{ maxHeight: 172, overflowY: 'auto' }}>
        {shown.map(e => (
          <button key={e} type="button" onClick={() => onChange(e)} className="center"
            style={{
              height: 42, fontSize: 22, borderRadius: 10,
              border: '1.5px solid ' + (value === e ? 'var(--green)' : 'var(--line)'),
              background: value === e ? '#e7f1ea' : '#fff', cursor: 'pointer',
            }}>{e}</button>
        ))}
        {shown.length === 0 && (
          <div style={{ gridColumn: '1 / -1', padding: '14px 4px', color: 'var(--muted)', fontSize: 13.5, fontWeight: 600 }}>
            Inget träffade „{q.trim()}” — prova t.ex. glass, korv, städ eller boll.
          </div>
        )}
      </div>
    </div>
  );
}

/* ---- app-styled confirm dialog (replaces window.confirm) ---- */
let _confirmSetter = null;

export function ConfirmHost() {
  const [req, setReq] = useState(null);
  _confirmSetter = setReq;
  if (!req) return null;
  const done = ok => { req.res(ok); setReq(null); };
  return (
    <div className="overlay" onMouseDown={e => { if (e.target === e.currentTarget) done(false); }}>
      <div className="modal" style={{ width: 'min(400px,100%)' }}>
        <div style={{ padding: '26px 26px 8px', display: 'flex', gap: 14, alignItems: 'flex-start' }}>
          <div className="center" style={{
            width: 44, height: 44, borderRadius: 13, flexShrink: 0,
            background: req.danger ? '#f8e2de' : '#fbeed8', color: req.danger ? 'var(--bad)' : 'var(--orange-deep)',
          }}>
            <Icon name="warn" size={22} />
          </div>
          <div>
            <div style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 19, marginBottom: 6 }}>{req.title || 'Är du säker?'}</div>
            <div style={{ fontSize: 14.5, color: 'var(--ink-soft)', lineHeight: 1.5, fontWeight: 600 }}>{req.message}</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', padding: '18px 24px 22px' }}>
          <button className="btn btn-ghost" onClick={() => done(false)}>Avbryt</button>
          <button className={'btn ' + (req.danger ? 'btn-danger' : 'btn-primary')}
            style={req.danger ? { background: 'var(--bad)', color: '#fff' } : {}}
            onClick={() => done(true)}>{req.confirmLabel || 'Ja, fortsätt'}</button>
        </div>
      </div>
    </div>
  );
}

export function askConfirm(message, opts = {}) {
  return new Promise(res => {
    if (_confirmSetter) _confirmSetter({ message, res, ...opts });
    else res(window.confirm(message));
  });
}

/* ---- transient success toast ---- */
export function Toast({ msg }) {
  if (!msg) return null;
  return (
    <div style={{
      position: 'absolute', bottom: 26, left: '50%', transform: 'translateX(-50%)', zIndex: 80,
      background: 'var(--green-deep)', color: '#fff', padding: '14px 22px', borderRadius: 14, fontWeight: 700,
      boxShadow: 'var(--shadow-lg)', display: 'flex', alignItems: 'center', gap: 10, animation: 'slideup .25s ease',
    }}>
      <Icon name="check" size={20} color="#a8e0bd" />{msg}
    </div>
  );
}

/* ---- error banner fed by the store ---- */
export function ErrorBanner({ message, onDismiss }) {
  if (!message) return null;
  return (
    <div style={{
      position: 'absolute', bottom: 26, left: '50%', transform: 'translateX(-50%)', zIndex: 90,
      background: 'var(--bad)', color: '#fff', padding: '14px 18px 14px 20px', borderRadius: 14, fontWeight: 700,
      boxShadow: 'var(--shadow-lg)', display: 'flex', alignItems: 'center', gap: 14, maxWidth: 'min(560px,90%)',
      animation: 'slideup .25s ease',
    }}>
      <Icon name="warn" size={20} />
      <span style={{ fontSize: 14.5, lineHeight: 1.4 }}>{message}</span>
      <button className="btn btn-icon" style={{ background: 'rgba(255,255,255,.16)', color: '#fff', width: 34, height: 34 }}
        onClick={onDismiss}><Icon name="x" size={17} /></button>
    </div>
  );
}

/* ---- väntöverlägg medan appen laddar eller sparar ----
   Utan det ser ett långsamt anrop ut som att appen hängt sig. Visas först
   efter en kort stund så snabba svar inte blinkar till, och blockerar
   trycken under tiden så ingen råkar slå in köpet två gånger. */
export function BusyOverlay({ active, label }) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (!active) {
      setShow(false);
      return undefined;
    }
    const t = setTimeout(() => setShow(true), 250);
    return () => clearTimeout(t);
  }, [active]);
  if (!show) return null;
  return (
    <div className="center" role="status" aria-live="polite" style={{
      position: 'absolute', inset: 0, zIndex: 85,
      background: 'rgba(20,46,29,.28)', backdropFilter: 'blur(3px)', WebkitBackdropFilter: 'blur(3px)',
      animation: 'fade .15s ease',
    }}>
      <div className="center" style={{ flexDirection: 'column', gap: 14, background: 'var(--card)', borderRadius: 22, padding: '26px 36px', boxShadow: 'var(--shadow-lg)', minWidth: 240, animation: 'pop .18s cubic-bezier(.2,.9,.3,1.3)' }}>
        <div className="spinner spinner-dark" style={{ width: 36, height: 36, borderWidth: 4 }} />
        <div style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 18, color: 'var(--green-deep)', textAlign: 'center' }}>{label || 'Ett ögonblick…'}</div>
        <div style={{ fontSize: 12.5, color: 'var(--muted)', fontWeight: 600 }}>Appen jobbar — vänta lite</div>
      </div>
    </div>
  );
}

/* ---- full-stage loading state ---- */
export function Loading({ label = 'Hämtar kioskdata…' }) {
  return (
    <div className="stage center" style={{ flexDirection: 'column', gap: 18 }}>
      <img src={LOGO} alt="SIF" style={{ width: 72, height: 72 }} />
      <div className="spinner spinner-dark" />
      <div style={{ fontWeight: 700, color: 'var(--muted)' }}>{label}</div>
    </div>
  );
}
